import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { addDaysIso, fmtDayMon, plural, relDay, todayIso } from '../lib/format';
import type { RestaurantRow } from '../lib/types';
import { Loading, Sheet } from './ui';
import { useToast } from './feedback';

/** Inicia (ou retoma) a visita. `data`: visita feita em outro dia; `agendaId`: liga ao agendamento. */
export function useStartVisit(tid: string) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const start = async (restaurantId: string, o: { data?: string; agendaId?: string } = {}) => {
    setBusy(o.agendaId ?? restaurantId);
    try {
      const r = await api.post<{ id: string; resumed: boolean }>(`/api/t/${tid}/visits`, { restaurantId, ...o });
      if (r.resumed) toast('Retomando a visita em andamento');
      qc.invalidateQueries({ queryKey: ['agenda', tid] });
      nav(`/t/${tid}/visita/${r.id}`);
    } catch (e) {
      toast(errMsg(e), 'err');
    } finally {
      setBusy(null);
    }
  };
  return { start, busy };
}

export function PickRestaurantSheet({ tid, open, onClose }: { tid: string; open: boolean; onClose: () => void }) {
  const q = useQuery({ queryKey: ['restaurants', tid, ''], queryFn: () => api.get<RestaurantRow[]>(`/api/t/${tid}/restaurants`), enabled: open });
  const { start, busy } = useStartVisit(tid);
  const [filter, setFilter] = useState('');
  const hoje = todayIso();
  const [dia, setDia] = useState(hoje);
  const [outroDia, setOutroDia] = useState(false);
  useEffect(() => { if (open) { setDia(todayIso()); setOutroDia(false); } }, [open]);
  const list = (q.data ?? []).filter((r) => !filter || r.nome.toLowerCase().includes(filter.toLowerCase()));
  return (
    <Sheet open={open} onClose={onClose} title="Qual restaurante?">
      <div className="row gap-8 wrap" style={{ marginBottom: 12, alignItems: 'center' }}>
        <span className="small">Dia da visita:</span>
        {!outroDia ? (
          <>
            <span className="pill res">Hoje</span>
            <button className="link-btn u" style={{ fontSize: 12.5 }} onClick={() => setOutroDia(true)}>foi em outro dia?</button>
          </>
        ) : (
          <>
            {[0, -1, -2].map((n) => {
              const d = addDaysIso(hoje, n);
              return <button key={n} type="button" className={`chip toggle sm ${dia === d ? 'on' : ''}`} onClick={() => setDia(d)}>{relDay(d, hoje)}</button>;
            })}
            <input className="input sm" type="date" max={hoje} value={dia} onChange={(e) => e.target.value && setDia(e.target.value)} style={{ width: 160 }} aria-label="Outra data" />
          </>
        )}
      </div>
      {(q.data?.length ?? 0) > 6 && (
        <input className="input sm" style={{ marginBottom: 10 }} placeholder="Buscar" value={filter} onChange={(e) => setFilter(e.target.value)} />
      )}
      {q.isLoading && <Loading rows={3} height={58} />}
      <div className="stack gap-8">
        {list.map((r) => (
          <button key={r.id} className="item" style={{ padding: 13, borderRadius: 11 }} onClick={() => start(r.id, dia !== hoje ? { data: dia } : {})} disabled={!!busy}>
            <div className="flex1">
              <div className="wrapline" style={{ fontSize: 14, fontWeight: 500 }}>{r.nome}</div>
              <div className="small" style={{ marginTop: 2 }}>
                {r.lastVisitAt ? `Última visita ${fmtDayMon(r.lastVisitAt)}` : 'Sem visitas'} · {plural(r.openNcs, 'pendência', 'pendências')}
              </div>
            </div>
            {busy === r.id ? <span className="spinner" style={{ color: 'var(--accent)' }} /> : <span className="none" style={{ color: 'var(--accent)' }}>→</span>}
          </button>
        ))}
        {q.data && q.data.length === 0 && <div className="empty">Nenhum restaurante liberado para você ainda.</div>}
      </div>
    </Sheet>
  );
}
